import { useCallback, useMemo } from 'react';

/**
 * useDraggable — public SDK hook that makes any element a drag source
 * for the widget library's drag/drop contract (Phase 3).
 *
 * Wraps native HTML5 drag events with a typed `DragPayload`. Serializes
 * the payload onto `dataTransfer` under a custom MIME type so accidental
 * drops on third-party drop zones never fire — only matching widget
 * drop targets (`useChartDropTarget`) read the payload back.
 *
 * Usage
 * -----
 *   const dragProps = useDraggable({
 *     type: 'procedure',
 *     source: 'rail-search',
 *     procedureId: 'proc-123',
 *     procedureName: 'Composite Filling',
 *   });
 *   return <button {...dragProps}>Composite Filling</button>;
 *
 * Click handlers still fire normally — `useDraggable` does not consume
 * pointer events. Consumers can compose a click flow AND a drag flow on
 * the same element.
 *
 * MVP scope
 * ---------
 * Native HTML5 DnD only. Mouse + keyboard. Touch (iPad chairside) lands
 * later via `dnd-kit` or a touch shim if usage data justifies it.
 */

export const ARGYA_DND_MIME = 'application/x-arogya-widget';

// ───────────────────────────────────────────────────────────────────────
// Phase 7 — shared active-drag state. The dragover spec strips
// `dataTransfer.getData(...)` payloads for security, so during drag
// nothing on the page can know WHAT is being dragged from the bus's
// payload alone. We stash the payload module-locally on dragstart so
// hover-time consumers (the floating cursor chip in ToothChartContainer,
// the diagnosis-row "Links to:" hint, etc.) can read the procedure name
// without waiting for drop.
//
// `getActiveDragPayload()` returns the current payload or null.
// `subscribeActiveDrag(cb)` fires on dragstart + dragend with the
// updated payload; returns an unsubscribe function.
// ───────────────────────────────────────────────────────────────────────

let _activeDragPayload = null;
const _activeDragListeners = new Set();

function _notifyActiveDrag() {
  _activeDragListeners.forEach((cb) => {
    try { cb(_activeDragPayload); }
    catch (_e) { /* listener errors must not break the drag */ }
  });
}

export function getActiveDragPayload() {
  return _activeDragPayload;
}

export function subscribeActiveDrag(cb) {
  if (typeof cb !== 'function') return () => {};
  _activeDragListeners.add(cb);
  return () => _activeDragListeners.delete(cb);
}

export default function useDraggable(payload) {
  const serialized = useMemo(() => {
    if (!payload || typeof payload !== 'object') return null;
    try { return JSON.stringify(payload); }
    catch (_e) { return null; }
  }, [payload]);

  const onDragStart = useCallback((e) => {
    if (!serialized) return;
    try {
      e.dataTransfer.setData(ARGYA_DND_MIME, serialized);
      // Some browsers require a 'text/plain' fallback for the drag image.
      // Include a human-readable name so paste-to-text fallbacks aren't
      // empty (no behaviour impact otherwise).
      const human = (payload && (payload.procedureName || payload.content || payload.type)) || 'arogya';
      e.dataTransfer.setData('text/plain', String(human));
      e.dataTransfer.effectAllowed = 'copy';
    } catch (_e) { /* Safari / private mode — drag aborts */ }
    // Phase 7 — publish the payload to the shared active-drag bus so
    // hover-time consumers can read it (the spec restricts dataTransfer
    // payloads to drop time).
    _activeDragPayload = payload;
    _notifyActiveDrag();
  }, [serialized, payload]);

  const onDragEnd = useCallback(() => {
    // Clear active payload on drag end (cancel OR successful drop).
    _activeDragPayload = null;
    _notifyActiveDrag();
  }, []);

  if (!serialized) {
    return { draggable: false };
  }

  return {
    draggable: true,
    onDragStart,
    onDragEnd,
    // Hint to assistive tech that this element can be picked up.
    'aria-grabbed': false,
    'data-arogya-drag-type': payload.type,
  };
}
